/* eslint-disable spaced-comment */
'use client'

import Image from 'next/image'
import Container from '@components/common/layout'
import { type Asset } from '@lib/__generated/sdk'
import { Fragment, useEffect, useRef } from 'react'
import { cn } from '@lib/utils'

interface AnimatingTextProps {
  backgroundImage?: Pick<
    Asset,
    '__typename' | 'url' | 'width' | 'height'
  > | null
  markdown?: string | null
}

interface Color {
  r: number
  g: number
  b: number
  a: number
}

const initialColor: Color = { r: 225, g: 208, b: 150, a: 1 } // #E1D096 from Figma
const intermediateColor: Color = { r: 255, g: 192, b: 0, a: 1 } // text-gold
const finalColor: Color = { r: 0, g: 0, b: 0, a: 1 }

const initialColorStyle = `rgba(${initialColor.r},${initialColor.g},${initialColor.b},${initialColor.a})`
const finalColorStyle = `rgba(${finalColor.r},${finalColor.g},${finalColor.b},${finalColor.a})`

/**
 * @param percent Fractional percent from 0 -> 1
 */
const interpolate = (start: number, end: number, percent: number): number =>
  start + (end - start) * percent

const interpolateColor = (
  start: Color,
  end: Color,
  strength: number,
): Color => {
  return {
    r: Math.round(interpolate(start.r, end.r, strength)),
    g: Math.round(interpolate(start.g, end.g, strength)),
    b: Math.round(interpolate(start.b, end.b, strength)),
    a: Math.min(1, Math.max(0, interpolate(start.a, end.a, strength))),
  }
}
const getColorStyle = (color: Color): string =>
  `rgba(${color.r},${color.g},${color.b},${color.a})`

export const AnimatingTextContainer = (props: AnimatingTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const isVisibleRef = useRef(false)
  const isUpdatingRef = useRef(false)

  const delay = 0.2

  const { backgroundImage, markdown } = props
  if (markdown == null)
    return <Container className='relative bg-white py-10'>&nbsp;</Container>

  const paragraphs = markdown.split('\n').filter((p) => p != null && p !== '')

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (containerRef.current == null) {
      return
    }

    const letterSpans = containerRef.current.querySelectorAll<HTMLSpanElement>(
      'span.animate-character',
    )
    const letterCount = letterSpans.length

    const rafUpdate = () => {
      if (!isVisibleRef.current || containerRef.current == null) return

      const containerBoundRect = containerRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const progress = -containerBoundRect.top / windowHeight
      const clampedProgress = progress < 0 ? 0 : progress > 1 ? 1 : progress
      const normalizedDelay = delay * (letterCount - 1)

      if (imageRef.current) {
        const imageTranslateY = interpolate(30, 0, clampedProgress)
        const imageOpacity = interpolate(1, 0.2, clampedProgress)
        imageRef.current.style = `transform: translateY(${imageTranslateY}%); opacity: ${imageOpacity};`
      }

      for (let index = 0; index < letterCount; index++) {
        const staggerAmount = (index / (letterCount - 1)) * normalizedDelay
        const scaledProgress = clampedProgress * (1 + normalizedDelay)
        const letterProgress = scaledProgress - staggerAmount
        const clampedLetterProgress =
          letterProgress < 0 ? 0 : letterProgress > 1 ? 1 : letterProgress

        if (letterProgress <= 0) {
          letterSpans[index].style.color = initialColorStyle
        } else if (letterProgress >= 1) {
          letterSpans[index].style.color = finalColorStyle
        } else {
          const newColor =
            letterProgress <= 0.3
              ? interpolateColor(
                  initialColor,
                  intermediateColor,
                  clampedLetterProgress / 0.3,
                )
              : interpolateColor(
                  intermediateColor,
                  finalColor,
                  (clampedLetterProgress - 0.3) / 0.7,
                )
          const style = getColorStyle(newColor)
          letterSpans[index].style.color = style
        }
      }
    }

    const rafCallback = () => {
      rafUpdate()
      isUpdatingRef.current = false
    }
    let rafCallbackRef: number | null = null

    const onScroll = () => {
      if (!isUpdatingRef.current) {
        isUpdatingRef.current = true
        rafCallbackRef = requestAnimationFrame(rafCallback)
      }
    }

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          isVisibleRef.current = entry.isIntersecting
        }
      },
      { rootMargin: '10% 0px' },
    )

    intersectionObserver.observe(containerRef.current)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      intersectionObserver.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (rafCallbackRef != null) {
        cancelAnimationFrame(rafCallbackRef)
      }
    }
  }, [containerRef])

  return (
    <Container
      ref={containerRef}
      className={cn(
        'relative h-[201vh] bg-white',
        '[&>.max-width]:mx-auto [&>.max-width]:w-full [&>.max-width]:max-w-none',
      )}
    >
      <div className='sticky top-0 flex h-[100vh] items-center overflow-hidden'>
        {backgroundImage?.url != null && (
          <Image
            ref={imageRef}
            alt=''
            role='presentation'
            className='pointer-events-none absolute left-0 top-[-15%] h-[130%] w-full object-cover object-top opacity-50 will-change-transform'
            src={backgroundImage.url}
            width={backgroundImage.width ?? 1500}
            height={backgroundImage.height ?? 1500}
            style={{ transform: 'translateY(30%)' }}
          />
        )}
        <h2
          className={cn(
            'relative text-balance py-4 text-center font-normal',
            'mx-auto w-[var(--max-width)] max-w-[90%]',
            'text-h4',
            'md:text-h3',
          )}
        >
          <div
            className={cn('[&>p:last-child]:mb-0 [&>p]:mb-[1em]')}
            style={{ color: initialColorStyle }}
          >
            {paragraphs.map((p, pIndex) => {
              const words = p.split(' ').filter((w) => w != null && w !== '')
              return (
                <p className='paragraph' key={pIndex}>
                  {words.map((word, wordIndex) => (
                    <Fragment key={wordIndex}>
                      <span className='word whitespace-nowrap'>
                        {[...word].map((character, characterIndex) => (
                          <span
                            key={characterIndex}
                            className='animate-character whitespace-nowrap'
                          >
                            {character}
                          </span>
                        ))}
                      </span>{' '}
                    </Fragment>
                  ))}
                </p>
              )
            })}
          </div>
        </h2>
      </div>
    </Container>
  )
}
